import { Flex, Text } from '@mantine/core';
import { createDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted';

import {
	GetInvestorInvestmentDetailsRes,
	InvestmentApprovalStatus,
	SecurityType,
} from '@dealstack/shared/lib/generated';
import { extractDecimalPrecisionFromUnitDenominator } from '@dealstack/shared/lib/format/number';

import { InvestmentTableColumn } from './investmentTable.types';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn.tsx';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge.tsx';
import { InvestmentRowActions } from './InvestmentRowActions';
import { GetInvestmentTableColumnsProps } from './investmentTableColumns.types';

export const getInvestmentTableColumns = ({
	tMipManage,
	mipId,
	getInvestmentReference,
	getSecurityTypeTranslation,
	onRevokeClick,
	onMessageClick,
}: GetInvestmentTableColumnsProps): ExtendedColumnDef<GetInvestorInvestmentDetailsRes>[] => {
	return [
		createTextColumn<GetInvestorInvestmentDetailsRes>({
			id: InvestmentTableColumn.investmentReference,
			headerText: tMipManage('investors.expanded_row.investment_reference'),
			columnLabel: tMipManage('investors.expanded_row.investment_reference'),
			enableSorting: false,
			accessorFn: (row) =>
				getInvestmentReference(
					row.investmentWorkflowComputedType,
					!!row.completedDate,
					row.completedDate ?? row.createdDate
				),
		}),
		createTextColumn<GetInvestorInvestmentDetailsRes>({
			id: InvestmentTableColumn.state,
			accessorKey: 'state',
			headerText: tMipManage('investors.expanded_row.state'),
			columnLabel: tMipManage('investors.expanded_row.state'),
			enableSorting: true,
			size: 120,
			minSize: 120,
			maxSize: 120,
			cell: ({ row }) => {
				const investment = row.original;
				const isPending = investment.state === InvestmentApprovalStatus.PENDING;
				return (
					<StatusBadge
						text={tMipManage(
							`investors.expanded_row.${investment.state.toLowerCase()}`
						)}
						color={isPending ? 'orange.6' : 'teal.8'}
					/>
				);
			},
		}),
		createNumberColumn({
			id: InvestmentTableColumn.totalInvestment,
			accessorKey: 'totalInvestment',
			headerText: tMipManage('investors.expanded_row.total_investment'),
			filterText: tMipManage('investors.expanded_row.total_investment'),
			columnLabel: tMipManage('investors.expanded_row.total_investment'),
			enableSorting: true,
			cell: ({ row }) => (
				<Flex justify="flex-end" w="100%">
					<Text>
						<AmountFormatted
							value={row.original.totalInvestment}
							currency={row.original.currency}
							showNegative={row.original.totalInvestment < 0}
						/>
					</Text>
				</Flex>
			),
		}),
		{
			id: InvestmentTableColumn.investmentUnits,
			header: () => (
				<Flex justify="flex-end" w="100%">
					<Text fw={600}>{tMipManage('investors.expanded_row.investment_units')}</Text>
				</Flex>
			),
			columnLabel: tMipManage('investors.expanded_row.investment_units'),
			enableSorting: false,
			cell: ({ row }) => {
				const investmentUnits = row.original.investmentUnits;
				return (
					<Flex direction="column" align="flex-end" w="100%">
						{Object.entries(investmentUnits).map(([securityTypeKey]) => {
							const { value, securityUnitDenominator } =
								investmentUnits[securityTypeKey] ?? {};
							return (
								<Flex key={securityTypeKey} align="center" gap="0.25rem">
									<Text>
										{getSecurityTypeTranslation(
											securityTypeKey as SecurityType
										)}
										:
									</Text>
									<AmountFormatted
										value={value}
										showCurrency={false}
										currency={row.original.currency}
										decimalScale={extractDecimalPrecisionFromUnitDenominator(
											securityUnitDenominator
										)}
									/>
								</Flex>
							);
						})}
					</Flex>
				);
			},
		},
		createDateColumn<GetInvestorInvestmentDetailsRes>({
			id: InvestmentTableColumn.completedDate,
			accessorKey: 'completedDate',
			headerText: tMipManage('investors.expanded_row.date_completed'),
			columnLabel: tMipManage('investors.expanded_row.date_completed'),
			enableSorting: true,
		}),
		{
			id: InvestmentTableColumn.actions,
			header: '',
			enableSorting: false,
			enableColumnFilter: false,
			enableHiding: false,
			size: 60,
			minSize: 60,
			maxSize: 60,
			cell: ({ row }) => {
				const investment = row.original;
				const isPending = investment.state === InvestmentApprovalStatus.PENDING;
				return (
					<InvestmentRowActions
						isPending={isPending}
						mipId={mipId}
						onMessageClick={onMessageClick}
						onRevokeClick={() => onRevokeClick(investment.investmentId)}
					/>
				);
			},
		},
	];
};
